import React, { useCallback, useEffect, useState } from "react";
import PropTypes from "prop-types";
import { Box, Button, Chip, CircularProgress, Typography } from "@material-ui/core";
import { getAvailability } from "../../services/appointments.api";

/**
 * Selector de huecos reales del día (doc 0396 · F0.8 · M0.8.1 y M0.8.2).
 *
 * QUÉ SUSTITUYE
 *
 * El modal pedía la hora en un campo libre y nadie comprobaba si ese hueco
 * existía. El motor de disponibilidad llevaba meses construido y **ningún
 * componente lo llamaba**: solo lo usaban el Agente IA y el chatbot. De ahí que se
 * pudiera crear una cita un domingo a las 3 de la madrugada desde la propia
 * interfaz, sin trampa alguna.
 *
 * Ahora la pantalla ofrece lo que la base dice que existe. La rejilla la calcula
 * `fn_calcular_slots_disponibles`, que ya tiene en cuenta franjas del profesional,
 * horario del negocio, ocupación, antelación mínima y horas ya pasadas.
 *
 * SIN PREFERENCIA DE PROFESIONAL
 *
 * Con `professionalId` vacío el motor responde en modo unión: el hueco existe si
 * al menos un profesional lo tiene libre. Por eso cada hueco muestra cuántos
 * quedan — un dato que el agente humano no tenía y que evita la pregunta «¿con
 * quién lo pongo?».
 */
function SlotPicker({ companyId, professionalId, serviceId, date, value, onPick, disabled }) {
  const [slots, setSlots] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const cargar = useCallback(async () => {
    if (!date) {
      setSlots([]);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const { data } = await getAvailability({
        date,
        professionalId: professionalId || undefined,
        serviceId: serviceId || undefined
      });
      setSlots(Array.isArray(data?.slots) ? data.slots : []);
    } catch (err) {
      // El motivo importa: «ese día no se atiende» no es lo mismo que «falló la
      // consulta», y el agente necesita distinguirlo para saber qué decirle al
      // paciente que tiene delante.
      setError(err?.response?.data?.error || "No se pudo consultar la disponibilidad");
      setSlots([]);
    } finally {
      setLoading(false);
    }
  }, [date, professionalId, serviceId]);

  useEffect(() => {
    cargar();
  }, [cargar]);

  if (!date) {
    return (
      <Typography variant="caption" color="textSecondary">
        Elige una fecha para ver los horarios disponibles.
      </Typography>
    );
  }

  if (loading) {
    return (
      <Box style={{ display: "flex", alignItems: "center", gap: 8, padding: "8px 0" }}>
        <CircularProgress size={18} />
        <Typography variant="caption" color="textSecondary">
          Consultando horarios…
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Typography variant="caption" style={{ color: "#f44336" }}>
        {error}
      </Typography>
    );
  }

  const libres = slots.filter(s => s.available);

  if (slots.length === 0) {
    return (
      <Typography variant="caption" color="textSecondary" style={{ fontStyle: "italic" }}>
        Ese día no hay atención. Puede ser un día cerrado, o que el profesional no
        tenga horario declarado.
      </Typography>
    );
  }

  if (libres.length === 0) {
    return (
      <Typography variant="caption" color="textSecondary" style={{ fontStyle: "italic" }}>
        Los {slots.length} horarios de ese día están ocupados.
      </Typography>
    );
  }

  return (
    <Box>
      <Typography variant="caption" color="textSecondary">
        {libres.length} de {slots.length} horarios libres
        {!professionalId && " · sin profesional asignado, se elegirá al que esté libre"}
      </Typography>
      <Box style={{ display: "flex", flexWrap: "wrap", gap: 6, marginTop: 6 }}>
        {slots.map(slot => {
          const seleccionado = value === slot.start;
          const ocupado = !slot.available;
          return (
            <Button
              key={slot.start}
              size="small"
              disabled={disabled || ocupado}
              variant={seleccionado ? "contained" : "outlined"}
              color={seleccionado ? "primary" : "default"}
              onClick={() => onPick(slot)}
              style={{ minWidth: 76, opacity: ocupado ? 0.4 : 1 }}
              title={
                ocupado
                  ? "Ocupado"
                  : slot.freeCount > 1
                  ? `${slot.freeCount} profesionales libres`
                  : "1 profesional libre"
              }
            >
              {slot.start}
              {!professionalId && slot.available && slot.freeCount > 1 && (
                <Chip
                  size="small"
                  label={slot.freeCount}
                  style={{ height: 16, marginLeft: 4, fontSize: "0.65rem" }}
                />
              )}
            </Button>
          );
        })}
      </Box>
    </Box>
  );
}

SlotPicker.propTypes = {
  companyId: PropTypes.number,
  /** `null` o vacío = sin preferencia: el motor responde en modo unión. */
  professionalId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  serviceId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  date: PropTypes.string,
  value: PropTypes.string,
  onPick: PropTypes.func.isRequired,
  disabled: PropTypes.bool
};

SlotPicker.defaultProps = {
  companyId: null,
  professionalId: null,
  serviceId: null,
  date: "",
  value: "",
  disabled: false
};

export default SlotPicker;
